/**
 * ControlDetailPage - affiche le detail d un controle scanned avec ses
 * preuves enrichies (Evidence v2).
 *
 * Route : /controls/:id
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Title3, Body1, Caption1, Badge, Card, CardHeader,
  Button, tokens, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular, ArrowDownload20Regular, ShieldCheckmark24Regular,
  ErrorCircle24Regular, Warning24Regular, Info24Regular, Eye20Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { useLatestScan } from '../hooks/useScans';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { controlsById, familyLabels } from '../domain/controls';
import { statusColors } from '../theme/brandTheme';
import { ControlDetailRichView } from '../components/ControlDetailRichView';
import { EvidencePreviewDialog } from '../components/EvidencePreviewDialog';

interface EvidenceQuery {
  method?: string;
  uri?:    string;
  scopes?: string[];
}

interface AffectedItem {
  [key: string]: unknown;
}

interface EvidenceReference {
  type?:      string;
  framework?: string;
  ref?:       string;
  label?:     string;
  url?:       string;
}

interface EvidenceV2 {
  CapturedAt?:     string;
  Source?:         string;
  Summary?:        string;
  Query?:          EvidenceQuery;
  AffectedItems?:  AffectedItem[];
  Recommendation?: string;
  References?:     EvidenceReference[];
  Raw?:            unknown;
}

interface CheckResult {
  ControlId:   string;
  Status?:     string;
  status?:     string;
  Score?:      number;
  score?:      number;
  Message?:    string;
  message?:    string;
  Evidences?:  EvidenceV2[];
}

export function ControlDetailPage() {
  const { t, i18n }       = useTranslation();
  const { id }            = useParams<{ id: string }>();
  const [params]          = useSearchParams();
  const { tenant }        = useActiveTenant();
  const latestScan        = useLatestScan(tenant.id);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [preview, setPreview] = useState(false);

  const control = id ? controlsById[id] : undefined;

  useEffect(() => {
    if (!id || !latestScan) return;
    const results = ((latestScan as unknown as { results?: CheckResult[] })?.results ?? []) as CheckResult[];
    const target = id.toLowerCase();
    const found = results.find(r => {
      const anyR = r as unknown as Record<string, unknown>;
      const cid = String(anyR.controlId ?? anyR.ControlId ?? '').toLowerCase();
      return cid === target;
    });
    setResult(found ?? null);
  }, [id, latestScan, params]);

  if (!control) {
    return (
      <div style={{ padding: 32 }}>
        <MessageBar intent="error">
          <MessageBarBody>{t('controls.detail.unknownControl', { id })}</MessageBarBody>
        </MessageBar>
        <Link to="/controls">{t('controls.detail.backToCatalogue')}</Link>
      </div>
    );
  }

  const anyResult = result as unknown as Record<string, unknown> | null;
  const status    = String(anyResult?.Status    ?? anyResult?.status    ?? '').toLowerCase();
  const score     = (anyResult?.Score   ?? anyResult?.score)   as number | null | undefined ?? null;
  const message   = String(anyResult?.Message   ?? anyResult?.message   ?? '');
  const evidences = (anyResult?.Evidences ?? anyResult?.evidences ?? []) as unknown[];
  const evidenceCount = evidences.length || (Number(anyResult?.EvidenceCount ?? anyResult?.evidenceCount) || 0);

  const statusConfig: Record<string, { color: string; icon: JSX.Element; label: string }> = {
    pass:    { color: statusColors.pass,    icon: <ShieldCheckmark24Regular primaryFill={statusColors.pass}/>,    label: t('controls.detail.statusLabel.pass') },
    fail:    { color: statusColors.fail,    icon: <ErrorCircle24Regular primaryFill={statusColors.fail}/>,        label: t('controls.detail.statusLabel.fail') },
    warning: { color: statusColors.warning, icon: <Warning24Regular primaryFill={statusColors.warning}/>,         label: t('controls.detail.statusLabel.warning') },
    manual:  { color: '#4A5E72',            icon: <Info24Regular primaryFill="#4A5E72"/>,                          label: t('controls.detail.statusLabel.manual') },
    error:   { color: '#8A2727',            icon: <ErrorCircle24Regular primaryFill="#8A2727"/>,                   label: t('controls.detail.statusLabel.error') },
  };
  const sc = statusConfig[status] ?? { color: tokens.colorNeutralForeground3, icon: null as unknown as JSX.Element, label: status };

  function exportJson() {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evidence-${id}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // CyFun (CCB) toujours en premier dans les references.
  const orderedRefs = [...control.references].sort(
    (a, b) => (b.framework === 'CyFun' ? 1 : 0) - (a.framework === 'CyFun' ? 1 : 0),
  );

  return (
    <div>
      <PageHeader
        title={`${control.id} — ${control.title}`}
        description={control.description}
        actions={
          <>
            <Link to="/controls" style={{ textDecoration: 'none' }}>
              <Button appearance="secondary" icon={<ArrowLeft20Regular />}>{t('controls.detail.catalogue')}</Button>
            </Link>
          </>
        }
      />

      {/* Deux blocs de taille égale côte à côte : Résultat | Références framework */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'stretch' }}>
        {/* Bloc Résultat */}
        <Card style={{ borderLeft: `4px solid ${result ? sc.color : tokens.colorNeutralStroke1}`, display: 'flex', flexDirection: 'column' }}>
          <CardHeader header={<Title3>{t('controls.detail.resultTitle', 'Résultat')}</Title3>} />
          {result ? (
            <div style={{ marginTop: 10, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                {sc.icon}
                <Title3 style={{ color: sc.color, margin: 0 }}>{sc.label}</Title3>
                <Badge appearance="filled" color="brand" style={{ marginLeft: 'auto' }}>
                  {t(`family.${control.family}`, { defaultValue: familyLabels[control.family] })}
                </Badge>
              </div>
              <Body1 block>{message}</Body1>
              {score !== null && (
                <Caption1
                  style={{ display: 'block', marginTop: 6, color: tokens.colorNeutralForeground3 }}
                  dangerouslySetInnerHTML={{
                    __html: t('controls.detail.scoreSeverity', {
                      score: (score * 100).toFixed(0),
                      sev:   t(`severity.${control.severity}`, { defaultValue: control.severity }),
                    }),
                  }}
                />
              )}
              <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Button appearance="primary" size="small" icon={<ArrowDownload20Regular />} onClick={exportJson}>
                  {t('controls.detail.exportEvidence')}
                </Button>
                <Button appearance="secondary" size="small" icon={<Eye20Regular />} onClick={() => setPreview(true)} disabled={!result}>
                  {t('controls.detail.previewEvidence', 'Prévisualiser')}
                </Button>
                <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                  {t('controls.detail.evidenceCount', { defaultValue: '{{n}} preuve(s) disponible(s)', n: evidenceCount })}
                </Caption1>
              </div>
            </div>
          ) : (
            <MessageBar intent="info" style={{ marginTop: 10 }}>
              <MessageBarBody>{t('controls.detail.noResult')}</MessageBarBody>
            </MessageBar>
          )}
        </Card>

        {/* Bloc Références framework */}
        <Card style={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader header={<Title3>{t('controls.detail.frameworkRefs')}</Title3>} />
          <div style={{ marginTop: 10 }}>
            {orderedRefs.map((r, i) => (
              <div key={i} style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <Badge appearance="outline">{r.framework}</Badge>
                <code style={{ fontFamily: 'Consolas, monospace', fontSize: 13 }}>{r.ref}</code>
                <span style={{ flex: 1, color: tokens.colorNeutralForeground2 }}>{r.label}</span>
                {(r as { cyfunLevel?: string }).cyfunLevel && (
                  <Badge appearance="filled" color={
                    (r as { cyfunLevel?: string }).cyfunLevel === 'Essential' ? 'danger' :
                    (r as { cyfunLevel?: string }).cyfunLevel === 'Important' ? 'warning' : 'success'
                  }>
                    {(r as { cyfunLevel?: string }).cyfunLevel}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Remédiation (pleine largeur) */}
      {control.remediation && (
        <div style={{
          marginBottom: 16, padding: 16,
          background: tokens.colorBrandBackground2,
          borderRadius: 8,
          borderLeft: `4px solid ${tokens.colorBrandForeground1}`,
        }}>
          <Caption1 style={{ display: 'block', marginBottom: 6, fontWeight: 700, color: tokens.colorBrandForeground1 }}>
            {t('controls.detail.remediationLabel')}
          </Caption1>
          <Body1>{control.remediation}</Body1>
        </div>
      )}

      {/* Preuves détaillées (pleine largeur) */}
      {evidences.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Title3 style={{ marginBottom: 12 }}>{t('controls.detail.evidencesTitle', { n: evidences.length })}</Title3>
          {(evidences as EvidenceV2[]).map((ev, idx) => (
            <EvidenceCard key={idx} evidence={ev} index={idx} />
          ))}
        </div>
      )}

      <ControlDetailRichView
        controlId={control.id}
        lang={(i18n.language as 'fr-BE' | 'en' | 'nl-BE' | 'de-BE') || 'fr-BE'}
      />

      <EvidencePreviewDialog
        open={preview}
        onOpenChange={setPreview}
        title={`${control.id} — ${t('controls.detail.previewEvidence', 'Prévisualiser')}`}
        evidence={result}
        fileName={`evidence-${id}.json`}
      />
    </div>
  );
}

function EvidenceCard({ evidence, index }: { evidence: EvidenceV2; index: number }) {
  const { t, i18n } = useTranslation();
  const [showRaw, setShowRaw] = useState(false);

  return (
    <Card style={{ marginBottom: 12 }}>
      <CardHeader
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Title3>{t('controls.detail.evidenceN', { n: index + 1 })}</Title3>
            {evidence.CapturedAt && (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {new Date(evidence.CapturedAt).toLocaleString(i18n.language)}
              </Caption1>
            )}
          </div>
        }
      />

      {evidence.Summary && (
        <Body1 style={{ marginTop: 8 }}>{evidence.Summary}</Body1>
      )}

      {evidence.Source && (
        <div style={{ marginTop: 12 }}>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, fontWeight: 600 }}>
            {t('controls.detail.evidence.source')}
          </Caption1>
          <code style={{
            fontFamily: 'Consolas, monospace', fontSize: 12,
            background: tokens.colorNeutralBackground2,
            padding: '4px 8px', borderRadius: 4,
            display: 'inline-block', marginTop: 4,
          }}>
            {evidence.Source}
          </code>
        </div>
      )}

      {evidence.Query && (evidence.Query.uri || evidence.Query.method) && (
        <div style={{ marginTop: 16 }}>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, fontWeight: 600 }}>
            {t('controls.detail.evidence.graphQuery')}
          </Caption1>
          <div style={{
            fontFamily: 'Consolas, monospace', fontSize: 12,
            background: '#0F2638', color: '#7EE0B8',
            padding: '8px 12px', borderRadius: 6,
            marginTop: 6, overflow: 'auto',
          }}>
            <span style={{ color: '#FFD700' }}>{evidence.Query.method ?? 'GET'}</span> {evidence.Query.uri}
          </div>
          {evidence.Query.scopes && evidence.Query.scopes.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {evidence.Query.scopes.map((s, i) => (
                <Badge key={i} appearance="outline" size="small" style={{ marginRight: 4 }}>
                  {s}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {evidence.AffectedItems && evidence.AffectedItems.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, fontWeight: 600, marginBottom: 6 }}>
            {t('controls.detail.evidence.affectedItems', { n: evidence.AffectedItems.length })}
          </Caption1>
          <div style={{ overflow: 'auto', maxHeight: 300, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: tokens.colorNeutralBackground2 }}>
                  {Object.keys(evidence.AffectedItems[0] as object).map(k => (
                    <th key={k} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600 }}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {evidence.AffectedItems.slice(0, 50).map((it, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${tokens.colorNeutralStroke3}` }}>
                    {Object.values(it).map((v, j) => (
                      <td key={j} style={{ padding: '6px 10px', fontFamily: 'Consolas, monospace' }}>
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {evidence.Recommendation && (
        <div style={{
          marginTop: 16, padding: 12,
          background: '#FFF3D6',
          borderLeft: '4px solid #D88A00',
          borderRadius: 6,
        }}>
          <Caption1 style={{ display: 'block', fontWeight: 700, color: '#8A6308', marginBottom: 4 }}>
            {t('controls.detail.evidence.recommendation')}
          </Caption1>
          <Body1>{evidence.Recommendation}</Body1>
        </div>
      )}

      {evidence.References && evidence.References.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Caption1 style={{ display: 'block', color: tokens.colorNeutralForeground3, fontWeight: 600, marginBottom: 6 }}>
            {t('controls.detail.evidence.references')}
          </Caption1>
          {evidence.References.map((r, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              {r.url ? (
                <a href={r.url} target="_blank" rel="noopener noreferrer"
                  style={{ color: tokens.colorBrandForeground1, textDecoration: 'none', fontSize: 13 }}>
                  → {r.label ?? r.url}
                </a>
              ) : (
                <span style={{ fontSize: 13 }}>{r.label}</span>
              )}
              {r.type && <Badge appearance="outline" size="small" style={{ marginLeft: 8 }}>{r.type}</Badge>}
            </div>
          ))}
        </div>
      )}

      {evidence.Raw && (
        <div style={{ marginTop: 12 }}>
          <Button appearance="subtle" size="small" onClick={() => setShowRaw(s => !s)}>
            {showRaw ? t('controls.detail.evidence.hideRaw') : t('controls.detail.evidence.showRaw')}
          </Button>
          {showRaw && (
            <pre style={{
              marginTop:    8,
              padding:      12,
              background:   '#0F2638',
              color:        '#D7E3EE',
              borderRadius: 6,
              fontSize:     11,
              fontFamily:   'Consolas, monospace',
              overflow:     'auto',
              maxHeight:    320,
            }}>
              {typeof evidence.Raw === 'string'
                ? evidence.Raw
                : JSON.stringify(evidence.Raw, null, 2)}
            </pre>
          )}
        </div>
      )}
    </Card>
  );
}
